import { Row, Col, Card, Empty } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import type { TrendData } from '@/types';

interface Props {
  data: TrendData | null;
  loading: boolean;
}

const SERVICE_COLORS: Record<string, string> = {
  generate: '#1677ff',
  optimize: '#52c41a',
  one_click: '#722ed1',
  analyze_intent: '#faad14',
  smart_complete: '#ff4d4f',
};

const SERVICE_LABELS: Record<string, string> = {
  generate: '生成',
  optimize: '优化',
  one_click: '一键生成',
  analyze_intent: '意图分析',
  smart_complete: '智能补全',
};

const formatDate = (date: string) => dayjs(date).format('MM-DD');

export default function AiCharts({ data, loading }: Props) {
  if (loading) return <Row gutter={16}><Col span={12}><Card loading /></Col><Col span={12}><Card loading /></Col></Row>;
  if (!data) return <Empty />;

  const serviceTypes = [...new Set(data.aiCalls.map(d => d.service_type))];
  const pivotData: Record<string, any>[] = [];
  const dateMap = new Map<string, Record<string, number>>();
  data.aiCalls.forEach(item => {
    if (!dateMap.has(item.date)) dateMap.set(item.date, {});
    dateMap.get(item.date)![item.service_type] = item.count;
  });
  dateMap.forEach((values, date) => {
    pivotData.push({ date, ...values });
  });

  return (
    <Row gutter={16}>
      <Col xs={24} lg={12}>
        <Card title="AI调用量趋势（按服务类型）" size="small">
          {pivotData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={pivotData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => dayjs(v).format('YYYY-MM-DD')} />
                <Legend formatter={(value) => SERVICE_LABELS[value] || value} />
                {serviceTypes.map(type => (
                  <Line
                    key={type}
                    type="monotone"
                    dataKey={type}
                    stroke={SERVICE_COLORS[type] || '#8c8c8c'}
                    name={type}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="图片生成趋势" size="small">
          {data.imageGeneration.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.imageGeneration}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => dayjs(v).format('YYYY-MM-DD')} />
                <Line type="monotone" dataKey="count" stroke="#722ed1" name="生成数量" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </Col>
    </Row>
  );
}
